import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { ExternalSource, HrisIntegrationType } from "../../../../utils/models";
import axiosInstance from "../../../../utils/networkWrapper";

/** Mirrors the backend `SftpDepartmentKeyStatus` enum. Only `ok` carries keys. */
export type SftpDepartmentKeyStatus = "ok" | "unsupported_source" | "file_not_found" | "unparseable";

export interface SftpDepartmentKey {
	key: string;
	/** null when the feed carries no description for this code — the UI then shows the bare code. */
	label: string | null;
	employee_count: number;
}

export interface SftpDepartmentKeysResponse {
	status: SftpDepartmentKeyStatus;
	resolved_file_key: string | null;
	keys: SftpDepartmentKey[];
}

/**
 * The department routing codes present in the connected SFTP file, for the Dept ID picker.
 *
 * Returns the whole response rather than just `keys`: `status` is what tells the caller whether an
 * empty list means "this feed has no department column" (normal) or "we could not read the file".
 *
 * `enabled` carries the caller's support check: the endpoint is support-only AND it downloads a file,
 * so an admin without support would otherwise fire one guaranteed-403 request per HRIS row. `retry` is
 * off for the same reason — a 403 or a missing file is a settled answer, not a blip worth three tries.
 */
export function useSftpDepartmentKeys({
	folder,
	file,
	source,
	type,
	enabled,
}: {
	folder: string | undefined;
	file: string | undefined;
	source: ExternalSource;
	type: HrisIntegrationType;
	enabled: boolean;
}) {
	return useQuery({
		queryKey: ["sftp-department-keys", source, type, folder, file],
		queryFn: async () => {
			const response = await axiosInstance.get<SftpDepartmentKeysResponse>("/integrations/sftp/department-keys", {
				params: { folder, file, source, type },
			});
			return response.data;
		},
		enabled: enabled && !!folder && !!file,
		retry: false,
		staleTime: 60_000,
	});
}

/** One child of a browsed SFTP prefix. `sizeBytes`/`lastModified` are null for a folder. */
export interface SftpBrowseEntry {
	name: string;
	is_folder: boolean;
	size_bytes: number | null;
	last_modified: string | null;
	/** False when this entry can't be a feed's target at all: a folder, or a file loose at the bucket root. */
	selectable: boolean;
	/**
	 * The (folder, file) pair this entry must be CONNECTED as — not always where it sits. An archived
	 * snapshot folds back to its inbox folder and the name it was archived under, which is the only pair
	 * a sync can read. Resolved by the backend (it owns the archive-stamp format); both null when
	 * `selectable` is false.
	 */
	select_folder: string | null;
	select_file: string | null;
}

interface SftpBrowseResponse {
	path: string;
	entries: SftpBrowseEntry[];
	truncated: boolean;
	in_archive: boolean;
	/**
	 * The folder half a filename TYPED at this path must be connected as — the path itself outside an
	 * archive, the inbox folder that owns it inside one. Null where no filename is routable: the bucket
	 * root, and a top-level `archived/`. Resolved server-side for the same reason as an entry's
	 * `select_folder` — the client must not have to know the archive layout.
	 */
	connect_folder: string | null;
}

export interface SftpFilePreview {
	resolved_file_key: string;
	/** True when a sync has already filed the delivery away and only the archived copy remains. */
	is_archived_copy: boolean;
	last_modified: string | null;
	columns: string[];
	rows: string[][];
	/** Which row of the file `rows` starts at — the grid numbers its rows from here, not from 1. */
	row_offset: number;
	/** Rows in the whole file, which is what the grid pages through. */
	total_rows: number;
	/** True when this page is not the whole file. */
	truncated: boolean;
}

export interface SftpCredentials {
	address: string;
	port: number;
	user: string;
	password: string;
}

/** Mirrors the backend `HrisSourceCapability`. */
export interface HrisSourceCapability {
	source: ExternalSource;
	file_based: boolean;
	/** Whether the source routes on a tenant/client identifier as well as the department code. */
	requires_network_id: boolean;
}

/**
 * The direct children of one SFTP prefix, for the file browser.
 *
 * `path` is the browsed prefix with no trailing slash; "" is the bucket root. Kept fresh for a
 * minute like the other pickers — a vendor delivery lands minutes apart at best, and the browser
 * re-fetches on every navigation anyway.
 */
export function useSftpBrowse(path: string, enabled: boolean) {
	return useQuery({
		queryKey: ["sftp-browse", path],
		queryFn: async () => {
			const response = await axiosInstance.get<SftpBrowseResponse>("/integrations/sftp/browse", { params: { path } });
			// The whole response, not just `entries`: `truncated` and `in_archive` are what let the dialog
			// say why a file can't be picked instead of silently disabling it.
			return response.data;
		},
		enabled,
		staleTime: 60_000,
	});
}

/**
 * One page of a delivered feed, for the read-only preview dialog.
 *
 * Paged rather than head-only: the whole file is reachable, and each page is its own cache entry, so
 * stepping back to a page already read costs nothing. `placeholderData` keeps the previous page on
 * screen while the next one loads — a file is re-read per page, so blanking the grid in between would
 * make paging read as "the file went away".
 *
 * `retry` is off: a missing or unreadable file is a settled answer the dialog renders, not a blip
 * worth three attempts at downloading a file that can run to tens of megabytes.
 */
export function useSftpFilePreview({
	folder,
	file,
	page,
	pageSize,
	enabled,
}: {
	folder: string | undefined;
	file: string | undefined;
	page: number;
	pageSize: number;
	enabled: boolean;
}) {
	return useQuery({
		queryKey: ["sftp-file-preview", folder, file, page, pageSize],
		queryFn: async () => {
			const response = await axiosInstance.get<SftpFilePreview>("/integrations/sftp/file-preview", {
				params: { folder, file, rows: pageSize, offset: page * pageSize },
			});
			return response.data;
		},
		enabled: enabled && !!folder && !!file,
		retry: false,
		staleTime: 60_000,
		placeholderData: keepPreviousData,
	});
}

/** Which HRIS sources are file-based, i.e. configurable end to end without engineering. */
export function useHrisSourceCapabilities(enabled: boolean) {
	return useQuery({
		queryKey: ["hris-source-capabilities"],
		queryFn: async () => {
			const response = await axiosInstance.get<{ sources: HrisSourceCapability[] }>("/integrations/hris/sources");
			return response.data.sources;
		},
		enabled,
		// The answer is derived from the syncher registry, so it only changes on deploy.
		staleTime: Number.POSITIVE_INFINITY,
	});
}

/** The live SFTP login for an existing folder. Only fetched once the caller has shown its warning. */
export function useSftpFolderCredentials(folder: string | undefined, enabled: boolean) {
	return useQuery({
		queryKey: ["sftp-folder-credentials", folder],
		queryFn: async () => {
			const response = await axiosInstance.get<SftpCredentials>(`/sftp/folders/${encodeURIComponent(folder as string)}/credentials`);
			return response.data;
		},
		enabled: enabled && !!folder,
		retry: false,
		// Never cached: a credential should not sit in the query cache after the dialog that asked for
		// it closes, and a re-open is one cheap request.
		gcTime: 0,
		staleTime: 0,
	});
}
